import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize, first } from 'rxjs';
import { PartnerIncomeTaxDocumentDto } from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { DataRoomHttpService } from '@services/http/data-room/data-room-http.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { openFilePreviewDialog } from '@shared/utils/file-preview-dialog.util';
import { TableActions, TableComponent } from '@src/app/shared/components/table/table.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@shared/shared.module';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { PartnerIncomeTaxStateService } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax-state.service';
import {
  DOCUMENT_KIND_LABELS,
  DOCUMENT_STATUS_TAGS,
  canSelectDocument,
  describeReadSelectedLabel,
  formatPersonName,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';
import {
  DataRoomDocumentsStore,
  MAX_DOCUMENTS_PER_READ,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/data-room-documents/data-room-documents.store';
import { PartnerIncomeTaxReadingClock } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax-reading-clock';
import { describeStageElapsed } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/reading-progress.util';

/** Linhas por página da lista; abaixo disso a paginação some. */
const PAGE_SIZE = 10;

const OPEN_DOCUMENT_ERROR =
  'Não conseguimos abrir o documento agora. Tente de novo ou abra pela pasta IRPF do Data Room.';

/**
 * Bloco "Declarações no Data Room" (adendo v3 do card 3515), mostrado na aba interna Documentos (e embaixo do estado
 * vazio da aba quando ainda não há sócio). A lista e a leitura moram no `DataRoomDocumentsStore`; aqui ficam a tabela,
 * a seleção e as ações. Caixas e botão só com `PARTNERINCOMETAX_UPDATE`.
 */
@Component({
  selector: 'app-pit-data-room-documents',
  standalone: true,
  imports: [SharedModule, VaultDateTimePipe],
  templateUrl: './data-room-documents.component.html',
  styleUrl: './data-room-documents.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataRoomDocumentsComponent {
  @ViewChild('table') private table?: TableComponent<PartnerIncomeTaxDocumentDto>;

  protected readonly state = inject(PartnerIncomeTaxStateService);
  protected readonly store = inject(DataRoomDocumentsStore);
  private readonly clock = inject(PartnerIncomeTaxReadingClock, { optional: true });
  private readonly dataRoom = inject(DataRoomHttpService);
  private readonly dialogService = inject(DialogService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  /** Pode abrir a pasta no Data Room (`DATAROOM_VIEW`). */
  readonly canOpenFolder = input(false);
  /** Rótulo do link/botão para a pasta (vem do link da aba: IRPF, Sócios PF ou raiz). */
  readonly folderLabel = input('Abrir a pasta IRPF');
  readonly openFolder = output<void>();

  /**
   * Pode abrir o PDF: a mesma permissão que o `POST data-room/download-link` exige (`DATAROOM_LIST_LINK_ATTACHAMENT`),
   * não a de ver a pasta. Sem ela, o nome fica como texto.
   */
  protected readonly canOpenFile = toSignal(
    inject(PermissionsService).hasPermission(Permissions.DataRoom.LIST_LINK_ATTACHAMENT),
    { initialValue: false }
  );

  protected readonly selected = signal<PartnerIncomeTaxDocumentDto[]>([]);
  /** Documento cujo link está sendo pedido (desabilita o nome enquanto isso). */
  protected readonly openingId = signal<string | null>(null);

  protected readonly statusTags = DOCUMENT_STATUS_TAGS;
  protected readonly kindLabels = DOCUMENT_KIND_LABELS;
  protected readonly personName = formatPersonName;
  protected readonly isSelectable = canSelectDocument;
  protected readonly pageSize = PAGE_SIZE;

  protected readonly columns: ColumnDefinition[] = [
    new ColumnDefinition({
      field: 'fileName',
      header: 'Arquivo',
      template: 'fileName',
      sortable: false,
    }),
    new ColumnDefinition({ field: 'kind', header: 'Tipo', template: 'kind', sortable: false }),
    new ColumnDefinition({
      field: 'partnerName',
      header: 'Sócio',
      template: 'partnerName',
      sortable: false,
    }),
    new ColumnDefinition({
      field: 'exerciseYear',
      header: 'Exercício',
      template: 'exerciseYear',
      sortable: false,
    }),
    new ColumnDefinition({
      field: 'status',
      header: 'Leitura',
      template: 'status',
      sortable: false,
    }),
  ];

  protected readonly readLabel = computed(() => describeReadSelectedLabel(this.selected().length));

  constructor() {
    // Seleção só do que ainda pode ser lido: uma linha que entrou na fila perde a caixa.
    effect(() => {
      const list = this.store.documents();
      untracked(() =>
        this.selected.update(items =>
          items.filter(item =>
            list.some(doc => doc.documentId === item.documentId && canSelectDocument(doc))
          )
        )
      );
    });
  }

  protected onSelection(event: TableActions<PartnerIncomeTaxDocumentDto>): void {
    this.selected.set((event?.items ?? []).filter(canSelectDocument));
  }

  /**
   * Atalho: marca os não lidos e os com erro. A seleção mora na tabela; depois de marcá-la, a lista vai de novo como
   * cópia para a tabela (OnPush) redesenhar as caixas, e ela mantém a seleção pela chave `documentId`.
   */
  protected selectPending(): void {
    const pending = this.store.pending().filter(canSelectDocument).slice(0, MAX_DOCUMENTS_PER_READ);
    this.table?.onSelectionChange(pending);
    this.store.refreshReference();
  }

  /**
   * Linha embaixo do selo de quem está em leitura (card 3772): "Aguardando há 30 s", "Começou há 1 min" ou o motivo de
   * espera do documento de apoio. O tempo avança com o relógio da aba; sem relógio (fora da aba), fica só a etapa.
   */
  protected stageText(item: PartnerIncomeTaxDocumentDto): string | null {
    if (item.status === 'processing') {
      return describeStageElapsed('reading', this.clock?.elapsedSeconds(item.startedAt) ?? null);
    }
    if (item.status !== 'queued') return null;
    // Na fila com motivo = documento de apoio esperando o IRPF: texto curto na linha, motivo inteiro no title (card 3773).
    if (item.message) return describeStageElapsed('waiting', null);
    return describeStageElapsed('queued', this.clock?.elapsedSeconds(item.requestedAt) ?? null);
  }

  /** Dica da linha embaixo do selo: o motivo completo do backend quando há, senão o próprio texto. */
  protected stageTitle(item: PartnerIncomeTaxDocumentDto, stage: string): string {
    return item.status === 'queued' && item.message ? item.message : stage;
  }

  /**
   * Abre o PDF na pré-visualização do Data Room (pedido do Bruno, 29/10/2026: "tem que dar para visualizar"). Antes a
   * lista só deixava marcar e ler de novo, e quem queria conferir a declaração tinha de ir até a pasta.
   */
  protected openDocument(item: PartnerIncomeTaxDocumentDto): void {
    this.openingId.set(item.documentId);
    this.dataRoom
      .getDownloadLinks([item.documentId], { inline: true })
      .pipe(
        first(),
        finalize(() => this.openingId.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: links => {
          const url = links?.[0];
          if (url) openFilePreviewDialog(this.dialogService, { url, fileName: item.fileName });
          else this.toast.showToastError(OPEN_DOCUMENT_ERROR);
        },
        error: () => this.toast.showToastError(OPEN_DOCUMENT_ERROR),
      });
  }

  protected readSelected(): void {
    this.store.read(this.selected(), () => {
      this.table?.clearSelection();
      this.selected.set([]);
    });
  }
}
